/**
 * Propojení projektu — kudy aplikace projektu volá platformu a jiné projekty
 * a kdo volá ji (Platform Hub / App hub). Jedno místo pro vypínač hubu, klíč
 * aplikace, granty na nástroje platformy, propojení s projekty v obou směrech,
 * příjemce volání a provoz za 7 dní. Data: platform-hub.connections.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowDownLeft, ArrowUpRight, ExternalLink, KeyRound, RefreshCw, Share2, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { Badge, Button, Input, Select, Toggle } from '../../../components/ui';

interface Grant {
  id: string;
  tool: string;
  kind: string;
  project_slug: string;
  peer_slug: string | null;
  enabled: boolean;
  roles: string[] | null;
  rate_limit?: { per_minute?: number; per_hour?: number; per_day?: number } | null;
}
interface Usage {
  tool: string;
  calls: number;
  errors: number;
  last_at: string;
  avg_ms: number | null;
}
interface PeerUsage {
  direction: 'in' | 'out';
  other: string;
  tool: string;
  calls: number;
  errors: number;
  last_at: string;
}
interface AccessKind { kind: string; label: string; count: number; runs_as: string }
interface ControlTool { name: string; available: boolean; description: string | null }
interface ProjectToolDesc { tool_id: string; name: string; qualified_name: string; version: number; operation: string; roles: string[] | null; description: string }
interface AccessBlock {
  kinds: AccessKind[];
  summary: { users: number; users_enabled: number; mcp_disabled: number; with_key: number; with_sso: number; db_grants: number } | null;
  policy: { default: 'allow' | 'deny'; entries: number; version: number } | null;
  project_tools: ProjectToolDesc[];
  control_tools: ControlTool[];
}
interface AppUser {
  entity_id: string;
  label: string;
  email: string | null;
  enabled: boolean;
  mcp_enabled: boolean;
  roles: string[];
  tools: string[] | null;
  kinds: string[];
  keys_active: number;
}
interface DbGrant { entity_id: string; label: string; level: string; enabled: boolean }
interface PolicyEntry { tool: string; roles: string[] | null; enabled: boolean; mfa?: boolean }
interface PolicyView {
  ok?: boolean;
  policy: { default: 'allow' | 'deny'; tools: PolicyEntry[] };
  platform_tools: Array<{ tool: string; exposed: boolean; policy_roles: string[] | null }>;
}
interface Connections {
  ok?: boolean;
  error?: string;
  project: string;
  access?: AccessBlock;
  hub_enabled: boolean;
  app_user_keys: { enabled: boolean; active: number; users: number };
  service_mcp: { enabled: boolean; sso_enabled: boolean; path: string; urls: string[] };
  app_key: { enabled: boolean; key_id: string | null; last_used: string | null; use_count: number; issued_at: string | null };
  platform_grants: Grant[];
  peer_out: Grant[];
  peer_in: Grant[];
  receiver: { registered: boolean; path: string | null; enabled: boolean; allowed_callers: string[] | null };
  usage_7d: Usage[];
  peer_usage_7d: PeerUsage[];
}

const box = 'rounded-xl border border-gray-800 bg-gray-900/40';
const fmt = (v: string | null) => (v ? new Date(v).toLocaleString('cs-CZ') : '—');

function Block({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className={`${box} p-3 space-y-2`}>
      <div>
        <div className='text-sm font-medium text-white'>{title}</div>
        {hint && <p className='text-[11px] leading-snug text-gray-500'>{hint}</p>}
      </div>
      {children}
    </div>
  );
}

export default function ProjectConnectionsPanel({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ['platform-hub', 'connections', slug];
  const { data, isLoading } = useQuery({
    queryKey: key,
    enabled: !!slug,
    queryFn: () => api.mcp<Connections>('platform-hub.connections', { project: slug }, { silent: true }),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ['platform-hub', 'app_users', slug] });
    qc.invalidateQueries({ queryKey: ['platform-hub', 'service_policy', slug] });
    qc.invalidateQueries({ queryKey: ['codable'] });
  };
  const call = async (tool: string, args: Record<string, unknown>) => {
    const r = await api.mcp<{ ok?: boolean; error?: string }>(tool, args);
    if (r?.ok === false) throw new Error(r.error ?? t('hubConn.error', 'chyba'));
    return r;
  };
  const mut = useMutation({
    mutationFn: async ({ tool, args }: { tool: string; args: Record<string, unknown> }): Promise<void> => {
      await call(tool, args);
    },
    onSuccess: refresh,
    onError: (e) => toast.error(String((e as Error).message)),
  });
  const [newTool, setNewTool] = useState('');
  const [newToolLimit, setNewToolLimit] = useState('');
  const [newPeer, setNewPeer] = useState('');
  const [newPeerTool, setNewPeerTool] = useState('');
  const [newPolicyTool, setNewPolicyTool] = useState('');
  const [newPolicyRoles, setNewPolicyRoles] = useState('');
  const users = useQuery({
    queryKey: ['platform-hub', 'app_users', slug],
    enabled: !!slug,
    queryFn: () => api.mcp<{ ok?: boolean; users: AppUser[]; db_grants: DbGrant[] }>('platform-hub.app_users', { project: slug }, { silent: true }),
  });
  const policy = useQuery({
    queryKey: ['platform-hub', 'service_policy', slug],
    enabled: !!slug,
    queryFn: () => api.mcp<PolicyView>('platform-hub.service_policy', { project: slug }, { silent: true }),
  });

  if (!slug) return <p className='text-sm text-gray-500'>{t('hubConn.noSlug', 'Projekt nemá slug — propojení nelze zobrazit.')}</p>;
  if (isLoading) return <p className='text-sm text-gray-500'>{t('hubConn.loading', 'Načítám propojení…')}</p>;
  if (!data || data.ok === false) return <p className='text-sm text-amber-400'>{data?.error ?? t('hubConn.error', 'chyba')}</p>;

  const limitText = (l: NonNullable<Grant['rate_limit']>) =>
    [
      l.per_minute ? t('hubConn.perMin', '{{n}}/min', { n: l.per_minute }) : null,
      l.per_hour ? t('hubConn.perHour', '{{n}}/h', { n: l.per_hour }) : null,
      l.per_day ? t('hubConn.perDay', '{{n}}/den', { n: l.per_day }) : null,
    ].filter(Boolean).join(', ');

  const grantRow = (g: Grant, other?: string) => (
    <div key={g.id} className='flex items-center gap-2 py-1.5 text-sm'>
      <Toggle
        value={g.enabled}
        saving={mut.isPending}
        onSave={(v) =>
          mut.mutateAsync({
            tool: 'platform-hub.grant_set',
            args: { project_slug: g.project_slug, tool: g.tool, kind: g.kind, peer_slug: g.peer_slug ?? undefined, enabled: v },
          })}
      />
      <span className='font-mono text-xs text-gray-200'>{g.tool}</span>
      {other && <span className='text-xs text-gray-500'>{other}</span>}
      {g.roles?.length ? <Badge variant='info'>{g.roles.join(', ')}</Badge> : null}
      {g.rate_limit ? <Badge variant='default'>{limitText(g.rate_limit)}</Badge> : null}
      <div className='flex-1' />
      <button
        type='button'
        className='text-gray-500 hover:text-red-400'
        aria-label={t('hubConn.remove', 'Odebrat')}
        onClick={() =>
          mut.mutate({
            tool: 'platform-hub.grant_set',
            args: { project_slug: g.project_slug, tool: g.tool, kind: g.kind, peer_slug: g.peer_slug ?? undefined, remove: true },
          })}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );

  const usageOut = data.peer_usage_7d.filter((u) => u.direction === 'out');
  const usageIn = data.peer_usage_7d.filter((u) => u.direction === 'in');

  return (
    <div className='space-y-3'>
      <div className={`${box} flex items-center gap-3 p-3`}>
        <Share2 size={16} className='shrink-0 text-gray-400' />
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-medium text-white'>{t('hubConn.hubTitle', 'Platform Hub projektu')}</div>
          <p className='text-[11px] leading-snug text-gray-500'>
            {t(
              'hubConn.hubHint',
              'Kudy aplikace volá nástroje platformy a jiné projekty (/acm/call) a kudy jí volají jiné projekty. Vypnutí zastaví obojí; granty a klíč zůstanou.',
            )}{' '}
            <Link to='/modules/platform-hub' className='text-plex hover:underline'>
              {t('hubConn.allProjects', 'Přehled všech projektů')} <ExternalLink size={10} className='inline' />
            </Link>
          </p>
        </div>
        <Toggle
          value={data.hub_enabled}
          saving={mut.isPending}
          onSave={(v) => mut.mutateAsync({ tool: 'platform-hub.project_hub_set', args: { project: slug, enabled: v } })}
        />
      </div>

      <Block
        title={t('hubConn.keyTitle', 'Klíč aplikace')}
        hint={t(
          'hubConn.keyHint',
          'Ztotožní projekt při volání /acm/ i bez přihlášeného uživatele. Klíč vkládá ingress, aplikace ho nikde nedrží.',
        )}
      >
        <div className='flex flex-wrap items-center gap-3 text-sm'>
          <Toggle
            value={data.app_key.enabled}
            saving={mut.isPending}
            onSave={(v) => mut.mutateAsync({ tool: 'platform-hub.app_key_set', args: { project: slug, enabled: v } })}
          />
          <KeyRound size={14} className='text-gray-500' />
          <span className='font-mono text-xs text-gray-300'>{data.app_key.key_id ?? '—'}</span>
          <span className='text-xs text-gray-500'>
            {t('hubConn.lastUsed', 'naposledy')} {fmt(data.app_key.last_used)} · {data.app_key.use_count}×
          </span>
          <div className='flex-1' />
          {data.app_key.enabled && (
            <Button
              size='sm'
              variant='secondary'
              disabled={mut.isPending}
              onClick={() => mut.mutate({ tool: 'platform-hub.app_key_set', args: { project: slug, enabled: true, rotate: true } })}
            >
              <RefreshCw size={12} /> {t('hubConn.rotate', 'Vydat nový')}
            </Button>
          )}
        </div>
      </Block>

      <Block
        title={t('hubConn.serviceMcpTitle', 'Služební MCP projektu')}
        hint={t(
          'hubConn.serviceMcpHint',
          'Služby aplikace pro její uživatele a jejich agenty (Claude, Cursor). Pouští jen účty aplikace: klíč uživatele aplikace, nebo SSO uživatele s účtem v aplikaci spárovaného. Systémový účet ACM sám nestačí. Nabídka = granty níže ∩ role uživatele v aplikaci.',
        )}
      >
        <div className='flex flex-wrap items-center gap-3 text-sm'>
          <Toggle
            value={data.service_mcp.enabled}
            saving={mut.isPending}
            onSave={(v) => mut.mutateAsync({ tool: 'platform-hub.service_mcp_set', args: { project: slug, enabled: v } })}
          />
          <span className='text-xs text-gray-400'>{t('hubConn.serviceMcpOn', 'zapnuto')}</span>
          <Toggle
            value={data.service_mcp.sso_enabled}
            saving={mut.isPending}
            disabled={!data.service_mcp.enabled}
            onSave={(v) => mut.mutateAsync({ tool: 'platform-hub.service_mcp_set', args: { project: slug, sso: v } })}
          />
          <span className='text-xs text-gray-400'>{t('hubConn.serviceMcpSso', 'přihlášení SSO')}</span>
        </div>
        {data.service_mcp.enabled && data.service_mcp.urls.map((u) => (
          <div key={u} className='font-mono text-xs text-gray-300'>{u}</div>
        ))}
        {data.service_mcp.enabled && data.service_mcp.urls.length === 0 && (
          <p className='text-xs text-gray-500'>{t('hubConn.serviceMcpNoDomain', 'Projekt zatím nemá doménu — adresa vznikne s ní.')}</p>
        )}
      </Block>

      <Block
        title={t('hubConn.userKeysTitle', 'Klíče uživatelů aplikace')}
        hint={t(
          'hubConn.userKeysHint',
          'Aplikace je vydává přes hub svým uživatelům (platform-hub.user_key_issue). Klíč patří uživateli aplikace; oprávnění = granty níže ∩ role uživatele v aplikaci. Vypnutí zastaví vydávání i použití.',
        )}
      >
        <div className='flex flex-wrap items-center gap-3 text-sm'>
          <Toggle
            value={data.app_user_keys.enabled}
            saving={mut.isPending}
            onSave={(v) => mut.mutateAsync({ tool: 'platform-hub.app_user_keys_set', args: { project: slug, enabled: v } })}
          />
          <span className='text-xs text-gray-400'>
            {t('hubConn.userKeysActive', 'aktivních klíčů')}: {data.app_user_keys.active} · {t('hubConn.userKeysUsers', 'uživatelů')}: {data.app_user_keys.users}
          </span>
        </div>
      </Block>

      <Block
        title={t('hubConn.grantsTitle', 'Nástroje platformy, které projekt smí volat')}
        hint={t('hubConn.grantsHint', 'Grant = nástroj nebo vzor (např. xamod.*). Role omezí grant na uživatele s danou rolí; klíč aplikace role nemá.')}
      >
        {data.platform_grants.length === 0 && <p className='text-xs text-gray-500'>{t('hubConn.none', 'Žádné.')}</p>}
        <div className='divide-y divide-gray-800/60'>{data.platform_grants.map((g) => grantRow(g))}</div>
        <form
          className='flex gap-2 pt-1'
          onSubmit={(e) => {
            e.preventDefault();
            if (!newTool.trim()) return;
            const perMinute = Number(newToolLimit);
            mut.mutate({
              tool: 'platform-hub.grant_set',
              args: {
                project_slug: slug,
                tool: newTool.trim(),
                kind: 'platform',
                ...(perMinute > 0 ? { rate_limit: { per_minute: perMinute } } : {}),
              },
            });
            setNewTool('');
            setNewToolLimit('');
          }}
        >
          <Input value={newTool} onChange={(e) => setNewTool(e.target.value)} placeholder='xamod.*' className='font-mono text-xs' />
          <Input
            value={newToolLimit}
            onChange={(e) => setNewToolLimit(e.target.value.replace(/\D/g, ''))}
            placeholder={t('hubConn.limitPlaceholder', 'limit/min')}
            title={t('hubConn.limitHint', 'Limit = kolik volání za minutu smí projekt provést; bez něj platí výchozí limit služby.')}
            className='w-24 text-xs'
            inputMode='numeric'
          />
          <Button size='sm' type='submit' disabled={mut.isPending || !newTool.trim()}>{t('hubConn.add', 'Přidat')}</Button>
        </form>
      </Block>

      <Block
        title={t('hubConn.peersTitle', 'Propojení s jinými projekty (App hub)')}
        hint={t('hubConn.peersHint', 'Kam tento projekt smí volat a kdo smí volat jeho. Volání se doručí příjemci v cílové aplikaci.')}
      >
        <div className='flex items-center gap-1 text-xs font-medium text-gray-400'>
          <ArrowUpRight size={12} /> {t('hubConn.out', 'Volá ven')}
        </div>
        {data.peer_out.length === 0 && <p className='text-xs text-gray-500'>{t('hubConn.none', 'Žádné.')}</p>}
        <div className='divide-y divide-gray-800/60'>{data.peer_out.map((g) => grantRow(g, `→ ${g.peer_slug}`))}</div>
        <form
          className='flex gap-2'
          onSubmit={(e) => {
            e.preventDefault();
            if (!newPeer.trim() || !newPeerTool.trim()) return;
            mut.mutate({
              tool: 'platform-hub.grant_set',
              args: { project_slug: slug, tool: newPeerTool.trim(), kind: 'peer', peer_slug: newPeer.trim() },
            });
            setNewPeer('');
            setNewPeerTool('');
          }}
        >
          <Input value={newPeer} onChange={(e) => setNewPeer(e.target.value)} placeholder={t('hubConn.peerSlug', 'projekt (slug)')} className='text-xs' />
          <Input value={newPeerTool} onChange={(e) => setNewPeerTool(e.target.value)} placeholder='crm.*' className='font-mono text-xs' />
          <Button size='sm' type='submit' disabled={mut.isPending || !newPeer.trim() || !newPeerTool.trim()}>{t('hubConn.add', 'Přidat')}</Button>
        </form>
        <div className='flex items-center gap-1 pt-2 text-xs font-medium text-gray-400'>
          <ArrowDownLeft size={12} /> {t('hubConn.in', 'Volají sem')}
        </div>
        {data.peer_in.length === 0 && <p className='text-xs text-gray-500'>{t('hubConn.none', 'Žádné.')}</p>}
        {data.peer_in.map((g) => (
          <div key={g.id} className='flex items-center gap-2 py-1 text-sm'>
            <span className='text-xs text-gray-400'>{g.project_slug} →</span>
            <span className='font-mono text-xs text-gray-200'>{g.tool}</span>
            {!g.enabled && <Badge variant='default'>{t('hubConn.off', 'vypnuto')}</Badge>}
          </div>
        ))}
        <div className='flex items-center gap-3 pt-2 text-sm'>
          <Toggle
            value={data.receiver.enabled}
            saving={mut.isPending}
            onSave={(v) => mut.mutateAsync({ tool: 'platform-hub.receiver_set', args: { project: slug, enabled: v } })}
          />
          <span className='text-xs text-gray-400'>
            {t('hubConn.receiver', 'Příjemce volání v aplikaci')}{' '}
            <span className='font-mono'>{data.receiver.path ?? '—'}</span>
            {data.receiver.allowed_callers?.length
              ? ` · ${t('hubConn.onlyFrom', 'jen od')} ${data.receiver.allowed_callers.join(', ')}`
              : ''}
          </span>
        </div>
      </Block>

      <Block
        title={t('hubConn.accessTitle', 'Přístupy k datům a MCP')}
        hint={t(
          'hubConn.accessHint',
          'Každé volání patří do jednoho druhu přístupu: uživatel aplikace (klíč / SSO) běží pod svou identitou a RLS, identita ACM s právem k databázi je správcovská cesta. Přístup po uživatelích řídí projekt sám.',
        )}
      >
        <div className='flex flex-wrap gap-2 text-xs'>
          {(data.access?.kinds ?? []).map((k) => (
            <span key={k.kind} className='rounded-md border border-gray-800 px-2 py-1 text-gray-300'>
              {k.label}: <span className='font-medium text-white'>{k.count}</span>
              <span className='text-gray-500'> · {k.runs_as}</span>
            </span>
          ))}
        </div>
        {users.data?.users?.length ? (
          <div className='divide-y divide-gray-800/60'>
            {users.data.users.map((u) => (
              <div key={u.entity_id} className='flex flex-wrap items-center gap-2 py-1.5 text-sm'>
                <Toggle
                  value={u.mcp_enabled}
                  saving={mut.isPending}
                  onSave={(v) =>
                    mut.mutateAsync({ tool: 'platform-hub.app_user_access_set', args: { project: slug, entity_id: u.entity_id, mcp_enabled: v } })}
                />
                <span className={`text-xs ${u.enabled ? 'text-gray-200' : 'text-gray-500 line-through'}`}>{u.label}</span>
                {u.email && <span className='text-xs text-gray-500'>{u.email}</span>}
                {u.kinds.map((k) => <Badge key={k} variant={k === 'sso' ? 'info' : 'default'}>{k === 'sso' ? 'SSO' : t('hubConn.kindKey', 'klíč')}</Badge>)}
                {u.roles.length > 0 && <Badge variant='info'>{u.roles.join(', ')}</Badge>}
                {u.tools && <span className='font-mono text-[11px] text-gray-400'>{u.tools.join(', ')}</span>}
                <div className='flex-1' />
                <span className='text-xs text-gray-500'>{u.keys_active}× {t('hubConn.kindKey', 'klíč')}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className='text-xs text-gray-500'>{t('hubConn.noUsers', 'Žádní uživatelé aplikace (supabase.users_sync).')}</p>
        )}
        {users.data?.db_grants?.length ? (
          <div className='pt-1 text-xs text-gray-400'>
            {t('hubConn.dbGrants', 'Právo k databázi v identitách')}:{' '}
            {users.data.db_grants.map((g) => (
              <span key={g.entity_id} className='mr-2'>
                {g.label} <Badge variant='info'>{g.level}</Badge>
              </span>
            ))}
          </div>
        ) : null}
      </Block>

      <Block
        title={t('hubConn.policyTitle', 'Politika služebního MCP')}
        hint={t(
          'hubConn.policyHint',
          'Co z grantů správce (a z vlastních nástrojů app.*) projekt svým uživatelům vydává a jakým rolím. Výchozí allow = granty platí beze změny; deny = jen uvedené.',
        )}
      >
        <div className='flex flex-wrap items-center gap-3 text-sm'>
          <span className='text-xs text-gray-400'>{t('hubConn.policyDefault', 'neuvedené nástroje')}</span>
          <Select
            size='sm'
            value={policy.data?.policy?.default ?? 'allow'}
            onChange={(e) => mut.mutate({ tool: 'platform-hub.service_policy_set', args: { project: slug, default: e.target.value } })}
            options={[
              { value: 'allow', label: t('hubConn.policyAllow', 'vydat (allow)') },
              { value: 'deny', label: t('hubConn.policyDeny', 'nevydat (deny)') },
            ]}
          />
        </div>
        <div className='divide-y divide-gray-800/60'>
          {(policy.data?.policy?.tools ?? []).map((e) => (
            <div key={e.tool} className='flex items-center gap-2 py-1.5 text-sm'>
              <Toggle
                value={e.enabled}
                saving={mut.isPending}
                onSave={(v) =>
                  mut.mutateAsync({ tool: 'platform-hub.service_policy_set', args: { project: slug, tools: [{ tool: e.tool, roles: e.roles, enabled: v }] } })}
              />
              <span className='font-mono text-xs text-gray-200'>{e.tool}</span>
              {e.roles?.length ? <Badge variant='info'>{e.roles.join(', ')}</Badge> : null}
              <button
                type='button'
                className={`rounded border px-1.5 py-0.5 text-[10px] ${e.mfa ? 'border-emerald-700 text-emerald-300' : 'border-gray-700 text-gray-500 hover:text-gray-300'}`}
                title={t('hubConn.policyMfaHint', 'Vyžadovat druhý faktor přihlášení (jen SSO uživatelé)')}
                onClick={() =>
                  mut.mutate({ tool: 'platform-hub.service_policy_set', args: { project: slug, tools: [{ tool: e.tool, roles: e.roles, enabled: e.enabled, mfa: !e.mfa }] } })}
              >
                {t('hubConn.policyMfa', '2FA')}
              </button>
              <div className='flex-1' />
              <button
                type='button'
                className='text-gray-500 hover:text-red-400'
                aria-label={t('hubConn.remove', 'Odebrat')}
                onClick={() => mut.mutate({ tool: 'platform-hub.service_policy_set', args: { project: slug, remove: [e.tool] } })}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <form
          className='flex gap-2 pt-1'
          onSubmit={(e) => {
            e.preventDefault();
            if (!newPolicyTool.trim()) return;
            const roles = newPolicyRoles.split(',').map((r) => r.trim()).filter(Boolean);
            mut.mutate({
              tool: 'platform-hub.service_policy_set',
              args: { project: slug, tools: [{ tool: newPolicyTool.trim(), roles: roles.length ? roles : null, enabled: true }] },
            });
            setNewPolicyTool('');
            setNewPolicyRoles('');
          }}
        >
          <Input value={newPolicyTool} onChange={(e) => setNewPolicyTool(e.target.value)} placeholder='xamod.* / app.claims_open' className='font-mono text-xs' />
          <Input value={newPolicyRoles} onChange={(e) => setNewPolicyRoles(e.target.value)} placeholder={t('hubConn.rolesPlaceholder', 'role, role')} className='text-xs' />
          <Button size='sm' type='submit' disabled={mut.isPending || !newPolicyTool.trim()}>{t('hubConn.add', 'Přidat')}</Button>
        </form>
        {policy.data?.platform_tools?.length ? (
          <div className='pt-1 text-xs text-gray-400'>
            {t('hubConn.policyEffect', 'Účinek na granty')}:{' '}
            {policy.data.platform_tools.map((p) => (
              <span key={p.tool} className='mr-2 font-mono'>
                {p.tool} <Badge variant={p.exposed ? 'success' : 'default'}>{p.exposed ? t('hubConn.exposed', 'vydáno') : t('hubConn.notExposed', 'nevydáno')}</Badge>
              </span>
            ))}
          </div>
        ) : null}
      </Block>

      <Block
        title={t('hubConn.projectToolsTitle', 'Nástroje projektu (acm_tools)')}
        hint={t(
          'hubConn.projectToolsHint',
          'Definice žijí v databázi projektu (tabulka acm_tools, kontrakt acm_tools_contract). Tady je jejich popisovač z katalogu: identita, verze, operace, role. Návod: knihovna Codable „Nástroje projektu“.',
        )}
      >
        {data.access?.project_tools?.length ? (
          <div className='divide-y divide-gray-800/60'>
            {data.access.project_tools.map((p) => (
              <div key={p.tool_id} className='flex flex-wrap items-center gap-2 py-1.5 text-sm'>
                <span className='font-mono text-xs text-gray-200'>{p.name}</span>
                <Badge variant={p.operation === 'write' ? 'warning' : 'default'}>{p.operation}</Badge>
                <span className='text-xs text-gray-500'>v{p.version}</span>
                {p.roles?.length ? <Badge variant='info'>{p.roles.join(', ')}</Badge> : null}
                <span className='min-w-0 flex-1 truncate text-xs text-gray-500'>{p.description}</span>
                <span className='font-mono text-[10px] text-gray-600'>{p.qualified_name}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className='text-xs text-gray-500'>{t('hubConn.noProjectTools', 'Projekt zatím nástroje nedeklaruje (migrace ze šablony acm_tools).')}</p>
        )}
      </Block>

      <Block
        title={t('hubConn.controlToolsTitle', 'Nástroje pro správu přístupů')}
        hint={t(
          'hubConn.controlToolsHint',
          'Jednotná sada podle typu databáze, stejná pro každý projekt: uživatelé, klíče, přístup po uživatelích, politika, právo k DB, chráněné tabulky, nástroje projektu. Volá je aplikace přes hub nebo správce z MCP.',
        )}
      >
        <div className='grid gap-1 sm:grid-cols-2'>
          {(data.access?.control_tools ?? []).map((c) => (
            <div key={c.name} className={`text-xs ${c.available ? 'text-gray-300' : 'text-gray-600'}`}>
              <span className='font-mono'>{c.name}</span>
              {c.description && <span className='text-gray-500'> — {c.description.length > 90 ? `${c.description.slice(0, 90)}…` : c.description}</span>}
            </div>
          ))}
        </div>
      </Block>

      <Block title={t('hubConn.usageTitle', 'Provoz za 7 dní')} hint={t('hubConn.usageHint', 'Z auditu — počty a chyby, bez obsahu volání.')}>
        {data.usage_7d.length === 0 && usageOut.length === 0 && usageIn.length === 0 && (
          <p className='text-xs text-gray-500'>{t('hubConn.noTraffic', 'Žádná volání.')}</p>
        )}
        {[...data.usage_7d.map((u) => ({ k: u.tool, label: u.tool, ...u })),
          ...usageOut.map((u) => ({ k: `o${u.other}${u.tool}`, label: `→ ${u.other}: ${u.tool}`, avg_ms: null, ...u })),
          ...usageIn.map((u) => ({ k: `i${u.other}${u.tool}`, label: `${u.other} →: ${u.tool}`, avg_ms: null, ...u }))].map((u) => (
            <div key={u.k} className='flex items-center gap-2 text-xs'>
              <span className='min-w-0 flex-1 truncate font-mono text-gray-300'>{u.label}</span>
              <span className='text-gray-400'>{u.calls}×</span>
              {u.errors > 0 && <Badge variant='error'>{u.errors} {t('hubConn.errors', 'chyb')}</Badge>}
              <span className='text-gray-500'>{fmt(u.last_at)}</span>
            </div>
          ))}
      </Block>
    </div>
  );
}
